import React, { useState, useEffect } from "react";
import { Clock, Calendar, Users, User, X } from "lucide-react";
import Modal from "react-modal";
import PlansModal from "./PlansModal";
import { useAuth } from "../context/AuthContext";
import { doc, updateDoc, arrayUnion, getDoc, increment } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { checkAccess } from "../utils/accessControl";
import { useTranslation } from "react-i18next";
import { autoJoinGroupFromClass } from "../utils/autoJoinGroupFromClass";

Modal.setAppElement("#root");

const useClassEnrollment = () => {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [error, setError] = useState(null);
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  const updateSessionStorage = (newEnrolledClass) => {
    const storedUser = JSON.parse(sessionStorage.getItem("user"));
    if (storedUser) {
      const updatedUser = {
        ...storedUser,
        enrolledClasses: [
          ...(storedUser.enrolledClasses || []),
          newEnrolledClass,
        ],
      };
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const enrollInClass = async (classId, userId, tutorId) => {
    if (!classId) {
      setError(t("exploreClassCard.errors.invalidClassId"));
      return false;
    }

    setIsEnrolling(true);
    setError(null);
    try {
      const classRef = doc(db, "classes", classId);
      const userRef = doc(db, "students", userId);
      const tutorRef = doc(db, "tutors", tutorId);

      const classDoc = await getDoc(classRef);
      const classData = classDoc.data();

      if (!classData) {
        throw new Error(t("exploreClassCard.errors.classNotFound"));
      }

      if (!classData.classDateTime || !classData.classDateTime.seconds) {
        throw new Error(t("exploreClassCard.errors.classDateNotSet"));
      }

      // Calculate total capacity and check if class is full
      const totalCapacity = 
        typeof classData.availableSpots !== "undefined"
          ? classData.availableSpots + (classData.classMemberIds?.length || 0)
          : undefined;
      const isFull =
        typeof totalCapacity !== "undefined" &&
        (classData.classMemberIds?.length || 0) >= totalCapacity;
      
      if (isFull) {
        throw new Error(t("exploreClassCard.errors.classFull"));
      }

      await Promise.all([
        updateDoc(classRef, {
          classMemberIds: arrayUnion(userId),
          availableSpots: increment(-1), // Atomically decrement available spots
        }),
        updateDoc(userRef, {
          enrolledClasses: arrayUnion(classId),
        }),
      ]);

      // Check if the adminId corresponds to an actual tutor or student document
      // For free group classes, the admin might be a student, not a tutor
      const tutorDoc = await getDoc(tutorRef);
      if (tutorDoc.exists()) {
        // Admin is a tutor
        await updateDoc(tutorRef, {
          tutorStudentIds: arrayUnion(userId),
        });
      } else {
        // Check if admin is a student (for free group classes)
        const studentAdminRef = doc(db, "students", tutorId);
        const studentAdminDoc = await getDoc(studentAdminRef);
        if (studentAdminDoc.exists()) {
          // Admin is a student - update their adminOfClasses instead
          await updateDoc(studentAdminRef, {
            adminOfClasses: arrayUnion(classId),
          });
        }
        // If neither tutor nor student document exists, skip the admin update
        // This prevents the "no document to update" error
      }

      // Update context and session storage
      if (user) {
        const updatedUser = {
          ...user,
          enrolledClasses: [...(user.enrolledClasses || []), classId],
        };
        setUser(updatedUser);
        updateSessionStorage(classId);
      }

      // Automatically add user to group if class belongs to a group
      if (user && setUser) {
        try {
          await autoJoinGroupFromClass(classId, userId, user, setUser);
        } catch (groupJoinError) {
          // Don't fail class enrollment if group join fails
          console.warn(
            "[ExploreClassCard] Failed to auto-join group:",
            groupJoinError,
          );
        }
      }

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsEnrolling(false);
    }
  };

  return {
    enrollInClass,
    isEnrolling,
    error,
    setError,
  };
};

const ExploreClassCard = ({
  classId,
  id,
  className,
  language,
  languageLevel,
  classDateTime,
  classType,
  classDuration,
  tutorId,
  tutorName,
  tutorImageUrl,
  classMemberIds = [],
  availableSpots,
  physicalClass,
  imageUrl,
  classDescription,
  adminId,
  adminName,
  adminImageUrl,
  groupId,
  recurrenceTypes,
  isBooked = false,
  cardHeight = "h-[360px]", // Match ClassCard height
  cardWidth = "w-full max-w-lg", // Match ClassCard width
  imageHeight = "h-[190px]", // Match ClassCard image height
  recurringSlots = [],
}) => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const { enrollInClass, isEnrolling, error, setError } = useClassEnrollment();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBookingConfirmationOpen, setIsBookingConfirmationOpen] =
    useState(false);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);

  // Ensure we have a valid classId (use id as fallback)
  const validClassId = classId || id;

  // Check if classDateTime is valid (not TBD)
  const hasValidDateTime =
    classDateTime &&
    typeof classDateTime === "object" &&
    classDateTime.seconds &&
    typeof classDateTime.seconds === "number";

  // Determine which user details to display based on isBammbuu flag
  const isPremium =
    classType === "Individual Premium" || classType === "Group Premium";

  const displayName = isPremium ? tutorName : adminName;
  const displayImage = isPremium ? tutorImageUrl : adminImageUrl;

  const formatTime = (timestamp) => {
    if (!timestamp || !timestamp.seconds)
      return t("exploreClassCard.labels.tbd");

    // Convert Firebase timestamp to a Date object
    const date = new Date(timestamp.seconds * 1000);

    // Format the time in UTC
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Add a helper to get the day name
  const getDayName = (timestamp) => {
    if (!timestamp || !timestamp.seconds)
      return t("exploreClassCard.labels.tbd");
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString("en-US", { weekday: "long" });
  };

  // Update formatDate to use day name for recurring classes
  const formatDate = (timestamp) => {
    if (
      recurrenceTypes &&
      Array.isArray(recurrenceTypes) &&
      recurrenceTypes.length > 0 &&
      recurrenceTypes[0] !== "One-time" &&
      recurrenceTypes[0] !== "None"
    ) {
      return getDayName(timestamp);
    }
    if (!timestamp || !timestamp.seconds)
      return t("exploreClassCard.labels.tbd");
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleCardClick = () => {
    if (!validClassId) {
      console.error("Invalid class ID");
      return;
    }
    navigate(`/newClassDetailsUser/${validClassId}`);
  };

  const handleBookClass = (e) => {
    e.stopPropagation();

    // Check for valid classId
    if (!validClassId) {
      toast.error(
        t("exploreClassCard.errors.invalidClassId") || "Invalid class ID",
      );
      return;
    }

    // Prevent booking if date/time is not set
    if (!hasValidDateTime) {
      toast.error(
        t("exploreClassCard.errors.classDateNotSet") ||
          "Class date is not set yet",
      );
      return;
    }

    setIsBookingConfirmationOpen(true);
  };

  const handleConfirmBooking = async () => {
    if (!user) {
      setError(t("exploreClassCard.errors.loginRequired"));
      return;
    }

    // Check for valid classId
    if (!validClassId) {
      setError(t("exploreClassCard.errors.invalidClassId"));
      return;
    }

    // Double-check no booking for TBD classes
    if (!hasValidDateTime) {
      setError(t("exploreClassCard.errors.classDateNotSet"));
      return;
    }

    // Check access for premium classes before allowing enrollment
    if (isPremium) {
      const accessCheck = checkAccess(user, "premium-class", classType);
      if (!accessCheck.hasAccess) {
        toast.error(accessCheck.reason);
        setIsBookingConfirmationOpen(false);
        setIsPlansModalOpen(true);
        return;
      }
    }

    const success = await enrollInClass(validClassId, user.uid, adminId);

    if (success) {
      setIsBookingConfirmationOpen(false);
      setIsModalOpen(false);
      toast.success(
        t("exploreClassCard.success.classBooked") ||
          "Class booked successfully!",
      );
    }
  };

  const [profileUrl, setProfileUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminProfile = async () => {
      if (!adminId) {
        setLoading(false);
        return;
      }

      try {
        // Try to fetch from tutors collection first
        let adminDoc = await getDoc(doc(db, "tutors", adminId));

        // If not found in tutors, try students collection
        if (!adminDoc.exists()) {
          adminDoc = await getDoc(doc(db, "students", adminId));
        }

        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          setProfileUrl(adminData.photoUrl || null);
        }
      } catch (error) {
        console.error("Error fetching admin profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminProfile();
  }, [adminId]);

  // Determine if the user has already booked this class
  const isAlreadyBooked =
    user &&
    (classMemberIds.includes(user.uid) ||
      (user.enrolledClasses && user.enrolledClasses.includes(validClassId)));
  // Calculate total capacity and check if full
  // Total capacity = availableSpots (remaining) + classMemberIds.length (booked)
  const totalCapacity = 
    typeof availableSpots !== "undefined"
      ? availableSpots + (classMemberIds?.length || 0)
      : undefined;
  const isClassFull =
    typeof totalCapacity !== "undefined" &&
    (classMemberIds?.length || 0) >= totalCapacity;

  // Helper to get the next available slot
  const getNextAvailableSlot = () => {
    if (!recurringSlots || recurringSlots.length === 0) return null;
    const now = new Date();
    // Find the first slot in the future
    const nextSlot = recurringSlots.find((slot) => {
      const slotDate = slot.createdAt
        ? new Date(slot.createdAt.seconds * 1000)
        : slot.seconds
          ? new Date(slot.seconds * 1000)
          : null;
      return slotDate && slotDate > now;
    });
    return nextSlot || null;
  };

  // Use next available slot for display if recurring
  const showRecurring =
    recurrenceTypes &&
    Array.isArray(recurrenceTypes) &&
    recurrenceTypes.length > 0 &&
    recurrenceTypes[0] !== "One-time" &&
    recurrenceTypes[0] !== "None";
  const nextSlot = getNextAvailableSlot();

  // If we don't have a valid class ID or a valid class name, don't render the card
  if (!validClassId || !className) {
    return null;
  }

  return (
    <>
      {/* Remove the max-w-sm constraint to match ClassCard */}
      <div
        className="hover:cursor-pointer"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleCardClick(e);
          }
        }}
      >
        <div
          className={`flex flex-col ${cardHeight} ${cardWidth} border ${
            isPremium ? "border-[#14b82c]" : "border-[#ffc71f]"
          } ${
            classType === "Individual Premium" ? "bg-[#e6fde9]" : "bg-white"
          } rounded-3xl p-2`}
        >
          {/* Image Section - match aspect ratio with ClassCard */}
          <div className={`relative w-full ${imageHeight}`}>
            <img
              alt={className}
              src={imageUrl || "/images/default-class.png"}
              className="h-full w-full rounded-b-3xl rounded-t-2xl object-cover"
            />
            {isPremium && (
              <img
                src="/images/bambuu-plus-tag.png"
                alt={t("exploreClassCard.labels.bambuuPlus")}
                className="absolute left-2 top-2 h-6 w-24 sm:h-8 sm:w-28"
              />
            )}
            <div className="absolute bottom-0 left-0 right-0 space-y-1 rounded-b-2xl bg-[#B9F9C2BF]/75 p-2 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <h2 className="ml-2 line-clamp-2 text-lg font-bold text-gray-800 sm:text-xl">
                  {className}
                </h2>
              </div>

              <div className="flex flex-wrap items-center justify-between">
                <div className="flex items-center space-x-2">
                  <img
                    src={
                      language === "English"
                        ? "/svgs/xs-us.svg"
                        : language === "Spanish"
                          ? "/svgs/xs-spain.svg"
                          : "/svgs/eng-spanish-xs.svg"
                    }
                    alt={
                      language === "English"
                        ? t("exploreClassCard.altText.usFlag")
                        : t("exploreClassCard.altText.spainFlag")
                    }
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-[#042f0c] sm:text-base">
                    {language}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Show 1:1 badge for individual premium classes */}
                  {classType === "Individual Premium" && (
                    <span className="rounded-full bg-[#fff885] px-2 py-[2px] text-xs font-medium sm:text-sm">
                      1:1
                    </span>
                  )}

                  {languageLevel !== "None" && (
                    <span className="rounded-full bg-[#fff885] px-2 py-1 text-xs sm:px-3 sm:text-sm">
                      {languageLevel}
                    </span>
                  )}
                </div>
              </div>
            </div>{" "}
          </div>

          {/* Details Section - using flex-1 to fill available space with justify-between */}
          <div className="flex w-full flex-1 flex-col justify-between px-3 pb-2 pt-2">
            <div className="flex w-full flex-col space-y-1.5">
              {/* Time and Date */}{" "}
              <div className="mt-2 flex w-full flex-row items-center justify-between">
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm text-[#454545]">
                    {showRecurring && nextSlot
                      ? formatTime(nextSlot.createdAt || nextSlot)
                      : formatTime(classDateTime)}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm text-[#454545]">
                    {showRecurring && nextSlot
                      ? formatDate(nextSlot.createdAt || nextSlot)
                      : formatDate(classDateTime)}
                  </span>
                </div>
              </div>
              {/* Admin and Spots */}{" "}
              <div className="flex w-full flex-row items-center justify-between">
                <div className="flex items-center space-x-1">
                  {profileUrl ? (
                    <img
                      src={profileUrl}
                      alt={adminName}
                      className="h-4 w-4 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-4 w-4 text-gray-600" />
                  )}
                  <span className="text-sm text-[#454545]">
                    {adminName || t("exploreClassCard.labels.tbd")}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <img
                    alt={t("exploreClassCard.altText.users")}
                    src="/svgs/users.svg"
                    className="h-4 w-4"
                  />
                  {true && (
                    <span className="text-sm text-[#454545]">
                      {classMemberIds.length}/
                      {availableSpots + classMemberIds.length}
                    </span>
                  )}
                </div>
              </div>
            </div>{" "}
            {/* Book Class Button - moved into the flex container */}
            <div className="mt-auto pt-2">
              {isAlreadyBooked || isClassFull ? (
                <button
                  className="w-full cursor-not-allowed rounded-full border border-gray-400 bg-gray-200 py-2 font-medium text-gray-500"
                  disabled
                  title={
                    isAlreadyBooked
                      ? t("exploreClassCard.labels.alreadyBooked")
                      : t("exploreClassCard.labels.classFull")
                  }
                >
                  {isAlreadyBooked
                    ? t("exploreClassCard.labels.alreadyBooked")
                    : t("exploreClassCard.labels.classFull")}
                </button>
              ) : (
                <button
                  onClick={handleBookClass}
                  className="w-full rounded-full border border-black bg-[#00B919] py-2 font-medium text-black hover:bg-[#00A117]"
                  disabled={!hasValidDateTime}
                >
                  {hasValidDateTime
                    ? t("exploreClassCard.labels.bookClass")
                    : t("exploreClassCard.labels.classNotScheduled")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        className="mx-auto mt-20 max-w-md rounded-3xl bg-white font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{
          content: {
            border: "none",
            padding: 0,
            maxWidth: "450px",
          },
        }}
      >
        <div className="">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-2xl font-semibold">
              {t("exploreClassCard.labels.classDetails")}
            </h2>
            <button
              onClick={() => setIsModalOpen(false)}
              className="rounded-full bg-gray-200 p-2 hover:bg-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Modal content... */}
          <div
            className={`${
              isPremium ? "bg-[#e6fde9]" : "bg-yellow-50"
            } mx-4 rounded-2xl px-6 pb-6 pt-8`}
          >
            {/* Profile image */}
            <div className="flex flex-col items-center">
              <div className="mb-4 h-24 w-24">
                <img
                  src={imageUrl || "/images/panda.png"}
                  alt={className}
                  className="h-full w-full rounded-full object-cover"
                />
              </div>

              <h2 className="mb-3 text-2xl font-bold">{className}</h2>

              {/* Language badge */}
              <div className="mb-6 flex items-center gap-2">
                <div className="h-6 w-6">
                  <img
                    src="/images/panda.png"
                    alt={language}
                    className="h-full w-full rounded-full"
                  />
                </div>
                <span>{language}</span>
                <span className="rounded-full bg-[#fff885] px-2 py-0.5 text-sm">
                  {languageLevel}
                </span>
              </div>

              {/* Time, Date, Users row */}
              <div className="mb-6 flex justify-center gap-8 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(classDateTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(classDateTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>
                    {classMemberIds?.length || 0}/
                    {availableSpots + (classMemberIds?.length || 0)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="mb-6 text-center text-gray-600">
                {classDescription}
              </p>

              {/* Language Group */}
              {groupId && (
                <div className="w-full">
                  <h3 className="mb-3 text-center text-lg font-bold">
                    {t("exploreClassCard.labels.languageGroup")}
                  </h3>
                  <div className="flex items-center gap-4 rounded-xl border border-[#97e3a2] bg-white p-4">
                    <img
                      src="/images/panda.png"
                      alt={`${language} flag`}
                      className="h-12 w-12 rounded-full"
                    />
                    <div className="flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="font-medium">
                          {t("exploreClassCard.labels.learners", { language })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <img
                          src="/images/panda.png"
                          alt={language}
                          className="h-4 w-4 rounded-full"
                        />
                        <span>{language}</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <img
                            src={displayImage || "/images/panda.png"}
                            alt="Leader"
                            className="h-4 w-4 rounded-full"
                          />
                          <span>{displayName}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{classMemberIds.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4">
            <button
              onClick={handleBookClass}
              className={`w-full rounded-full border py-3 font-medium ${
                hasValidDateTime
                  ? "border-[#042f0c] bg-[#14b82c] text-black hover:bg-[#119924]"
                  : "cursor-not-allowed border-gray-400 bg-gray-200 text-gray-500"
              }`}
              disabled={!hasValidDateTime}
            >
              {hasValidDateTime
                ? t("exploreClassCard.labels.bookClass")
                : t("exploreClassCard.labels.classNotScheduled")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isBookingConfirmationOpen}
        onRequestClose={() => setIsBookingConfirmationOpen(false)}
        className="mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{
          content: {
            border: "none",
            padding: "24px",
            maxWidth: "420px",
          },
        }}
      >
        <div className="text-center">
          <h2 className="mb-4 text-xl font-semibold">
            {t("exploreClassCard.confirmBooking.title")}
          </h2>
          {error && <p className="mb-4 text-red-600">{error}</p>}
          <p className="mb-6 text-gray-600">
            {classType === "Individual Premium"
              ? t(
                  "exploreClassCard.confirmBooking.descriptionPremiumIndividual",
                )
              : t("exploreClassCard.confirmBooking.description")}
          </p>
          <div className="flex flex-row gap-2">
            <button
              onClick={() => setIsBookingConfirmationOpen(false)}
              className="w-full rounded-full border border-gray-300 py-2 font-medium hover:bg-gray-50"
            >
              {t("exploreClassCard.confirmBooking.cancel")}
            </button>
            <button
              onClick={handleConfirmBooking}
              className="w-full rounded-full border border-[#042f0c] bg-[#14b82c] py-2 font-medium text-black hover:bg-[#119924]"
            >
              {isEnrolling
                ? t("exploreClassCard.confirmBooking.enrolling")
                : t("exploreClassCard.confirmBooking.confirm")}
            </button>
          </div>
        </div>
      </Modal>
      <PlansModal
        isOpen={isPlansModalOpen}
        onClose={() => setIsPlansModalOpen(false)}
      />
    </>
  );
};

export default ExploreClassCard;
